<template>
  <div class="border-bottom pb-3">
    <div v-if="!autocompleteDisabled" class="form-group" style="position: relative;">
      <div class="d-flex">
        <label>{{articleLabel}}</label>
      </div>
      <autocomplete-article
        :class="{ 'is-invalid': highlightFields && (!value.article || !value.article.id) && !articleIsOptional && !optional }"
        :original-title="value.article ? value.article.title : ''"
        @input="selectedArticle"
        :disable-private="disablePrivate"
        :strip="strip"
      />
    </div>

    <form-control
      class="mb-3"
      :value="value.title"
      :errors="(highlightFields && !value.title && (!optional || optional && value.synopsis)) ? [' '] : []"
      :no-margin="true"
      :title="titleLabel"
      :placeholder="`Enter ${titleLabel}`"
      @input="updateTitle"
    />

    <div class="form-group" v-if="!withoutSynopsis">
      <div class="d-flex justify-content-between">
        <label>{{synopsisLabel}}</label>

        <span
          :class="{ 'text-danger': synLength > synopsisMaxLength }"
        >{{ synLength }}/{{ synopsisMaxLength }}</span>
      </div>

      <HtmlEditor
        :value="value.synopsis"
        class="mb-3"
        :class="{ 'is-invalid': (highlightFields && !value.synopsis) || synLength > synopsisMaxLength }"
        @input="updateSynopsis"
        :token="`Bearer ${$login.getToken()}`"
        :image-upload-url="imageUploadUrl"
        :file-upload-url="fileUploadUrl"
      />
    </div>

    <div v-if="withImage">
      <div class="form-group">
        <label>{{imageUploadLabel}}</label>
        <img
          v-if="value.image"
          :src="value.image.url"
          :alt="value.image.name"
          class="d-block mb-2"
          style="max-width: 520px;"
        />
        <FileUpload
          :value="value.image ? [value.image] : []"
          :headers="headers"
          :action="`${url}upload/image`"
          @input="pictureUploaded"
          :accept="['image/png', 'image/jpeg']"
        />
      </div>
    </div>

    <a
      v-if="removable"
      @click="$emit('remove')"
      class="btn btn-outline-danger text-danger ml-auto"
    >Remove Article</a>
  </div>
</template>

<script>
import he from 'he'
import _ordinal from 'ordinal'
import striptags from 'striptags'

import { HtmlEditor, FileUpload } from '@frontend/components'
import gateway from '@/gateway'
import AutocompleteArticle from '@/components/AutocompleteArticle'
import {apolloClient} from '../../../apollo'
import gql from 'graphql-tag'

function ordinal(num) {
  return _ordinal(typeof num === 'string' ? parseInt(num, 10) : num)
}

export default {
  components: {
    AutocompleteArticle,
    FileUpload,
    HtmlEditor
  },
  props: {
    customArticleLabel: { type: String },
    customSynopsisLabel: { type: String },
    customTitleLabel: { type: String },
    customImageUploadLabel: { type: String },
    articleIsOptional: {
      default: false,
      type: Boolean
    },
    strip: {
      default: false,
      type: Boolean,
    },
    optional: {
      default: false,
      type: Boolean
    },
    disablePrivate: {
      type: Boolean,
      default: false
    },
    withoutSynopsis: {
      type: Boolean,
      default: false
    },
    useSummarizer: {
      type: Boolean,
      default: false
    },
    useBullets: {
      type: [Boolean, String],
      default: false
    },
    withImage: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      required: false,
      default() {
        return {
          title: '',
          synopsis: ''
        }
      }
    },
    removable: {
      type: Boolean,
      required: false,
      default: false
    },
    id: {
      type: [Number, String]
    },
    highlightFields: {
      required: false,
      type: Boolean,
      default: false
    },
    autocompleteDisabled: {
      type: Boolean,
      default: false
    },
    labelsPrefix: {
      type: Function,
      default: (id) => ordinal(id)
    },
    striptags: {
      type: [Boolean, Array, String],
      default: false
    }
  },
  computed: {
    headers() {
      return {
        Authorization: 'Bearer ' + this.$login.getToken()
      }
    },
    synLength() {
      return (this.value.synopsis || '').replace(/<\/?[^>]+(>|$)/g, '').length
    },

    articleLabel() {
      if (this.customArticleLabel) {
        return this.customArticleLabel
      }
      if (!this.id) {
        return 'Article'
      }
      return `${this.labelsPrefix(this.id)} Article`
    },

    synopsisLabel() {
      if (this.customSynopsisLabel) {
        return this.customSynopsisLabel
      }
      if (!this.id) {
        return 'Synopsis'
      }
      return `${this.labelsPrefix(this.id)} Synopsis`
    },

    titleLabel() {
      if (this.customTitleLabel) {
        return this.customTitleLabel
      }
      if (!this.id) {
        return 'Title'
      }
      return `${this.labelsPrefix(this.id)} Title`
    },

    imageUploadLabel() {
      if (this.customImageUploadLabel) {
        return this.customImageUploadLabel
      }
      if (!this.id) {
        return 'Article image upload'
      }
      return `${this.labelsPrefix(this.id)} article image upload`
    }
  },
  data() {
    return {
      synopsisMaxLength: 700,
      error: null,
      url: process.env.VUE_APP_API_ENDPOINT,
      imageUploadUrl: process.env.VUE_APP_API_ENDPOINT + 'upload/image',
      fileUploadUrl: process.env.VUE_APP_API_ENDPOINT + 'upload/file'
    }
  },

  methods: {
    async getSummary(article) {
      try {
        const response = await gateway.post('summarizer', {
          model: 'xlnet-base-cased',
          ratio: 0.15,
          min_length: 25,
          max_length: 400,
          body: article.proBody
        })

        var tmp = document.createElement('DIV')
        tmp.innerHTML = response.data

        return (tmp.textContent || tmp.innerText || '')
          .substr(0, this.synopsisMaxLength)
          .replace('\n', '<br>')
      } catch (e) {
        console.log(e, e.response)
        return ''
      }
    },
    stripTags(text) {
      if (this.striptags === false) {
        return text
      }

      return this.striptags ? striptags(text, this.striptags) : striptags(text)
    },
    async getSynopsis(article) {
      const response = await apolloClient.query({
        query: gql`
          query {
            newArticle(id: ${article.id}) {
              value {
                proBody
                proBulletOne
                proBulletTwo
                proBulletThree
                starterBulletOne
                starterBulletTwo
                starterBulletThree
                proBody
                summary
              }
            }
          }
        `
      })
      article = response.data.newArticle.value
      const clean = (txt) =>
        this.stripTags(txt).substr(0, this.synopsisMaxLength)
      if (this.useSummarizer) {
        return clean(await this.getSummary(article))
      }

      if (this.useBullets) {
        if (this.useBullets === 'marketing') {
          if (!article.starterBulletOne && !article.starterBulletTwo && !article.starterBulletThree) {
            return `<p>${article.summary || article.proBody }}}</p>`
          }
          let bullets = ''
          if (article.starterBulletOne) {
            bullets += `<p><span class="syn-bullet">&bull;</span> ${article.starterBulletOne}</p>`
          }
          if (article.starterBulletTwo) {
            bullets += `<p><span class="syn-bullet">&bull;</span> ${article.starterBulletTwo}</p>`
          }
          if (article.starterBulletThree) {
            bullets += `<p><span class="syn-bullet">&bull;</span> ${article.starterBulletThree}</p>`
          }
          return bullets
        }
        if (!article.proBulletOne && !article.proBulletTwo && !article.proBulletThree) {
          return `<p>${article.summary || article.proBody }}}</p>`
        }
        let bullets = ''
        if (article.proBulletOne) {
          bullets += `<p><span class="syn-bullet">&bull;</span> ${article.proBulletOne}</p>`
        }
        if (article.proBulletTwo) {
          bullets += `<p><span class="syn-bullet">&bull;</span> ${article.proBulletTwo}</p>`
        }
        if (article.proBulletThree) {
          bullets += `<p><span class="syn-bullet">&bull;</span> ${article.proBulletThree}</p>`
        }
        return bullets
      }

      return clean(he.decode(article.proBody || '') || '')
    },
    async selectedArticle(article) {
      const newValue = {}
      if (article) {
        const { id, slug, title } = article
        const data = {
          id,
          slug,
          title,
          contentType: article.contentType,
          channel: article.channel,
          analyst: article.leadAnalyst ? {
            name: article.leadAnalyst.name,
            job_title: article.leadAnalyst.jobTitle,
            picture_url: article.leadAnalyst.picture.square,
            company: article.leadAnalyst.channel ? article.leadAnalyst.channel.name : null,
            company_slug: article.leadAnalyst.channel
              ? article.leadAnalyst.channel.slug
              : null,
            slug: article.leadAnalyst.slug
          } : null,
        }

        newValue.article = data
        newValue.title = article.title
        newValue.synopsis = await this.getSynopsis(article)
      } else {
        newValue.article = null
        newValue.title = ''
        newValue.synopsis = ''
      }
      this.$emit('input', newValue)
    },

    updateSynopsis(val) {
      const newValue = {...this.value}
      newValue.synopsis = val
      this.$emit('input', newValue)
    },

    updateTitle(val) {
      const newValue = {...this.value}
      newValue.title = val
      this.$emit('input', newValue)
    },

    selectedTextColor(color) {
      const newValue = {...this.value}
      newValue.textColor = color
      this.$emit('input', newValue)
    },

    selectedBackgroundColor(color) {
      const newValue = {...this.value}
      newValue.backgroundColor = color
      this.$emit('input', newValue)
    },

    pictureUploaded(fileList) {
      const newValue = {...this.value}
      const img = !fileList[0]
        ? null
        : {
            name: fileList[0].name,
            url: fileList[0].url
          }

      newValue.image = img
      this.$emit('input', newValue)
    }
  }
}
</script>
